import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { ROUTES } from '../../constants/routes';
import { captureAcquisitionSession } from '../../services/acquisitionSession';
import {
  buildLoginRedirectOptions,
  consumePendingAuthReturnTo,
} from '../../utils/authRedirect';
import { markGrowthSignupStarted } from '../../services/growthFunnelAnalytics';
import ErrorHandler from '../../utils/errorHandler';
import { isLocalAuthBypassEnabled } from '../../config/localAuthBypass';

const SIGNUP_ENTRY_PATHS = new Set([
  ROUTES.NUEVA_EMPRESA,
  ROUTES.IMPORT_EMPRESA,
]);

const AuthWrapper = ({ children }) => {
  const { isAuthenticated, isLoading, error, user, loginWithRedirect } =
    useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLocalAuthBypass = isLocalAuthBypassEnabled();

  // Sync Auth0 token with API service (tokens stored in memory, not localStorage)
  useAuthenticatedApi();

  // Set Sentry user context for error tracking
  useEffect(() => {
    if (isAuthenticated && user) {
      ErrorHandler.setUserContext(user);
    } else {
      ErrorHandler.clearUserContext();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const pendingReturnTo = consumePendingAuthReturnTo();
    if (!pendingReturnTo) {
      return;
    }

    const currentUrl = `${location.pathname}${location.search}${location.hash}`;
    if (pendingReturnTo !== currentUrl) {
      navigate(pendingReturnTo, { replace: true });
    }
  }, [
    isAuthenticated,
    location.hash,
    location.pathname,
    location.search,
    navigate,
  ]);

  // Check if current path is an auth route (sign-in, sign-up, etc.)
  const isAuthRoute = location.pathname.startsWith('/auth');
  const shouldRedirectToLogin =
    !isLocalAuthBypass && !isLoading && !error && !isAuthenticated && !isAuthRoute;

  useEffect(() => {
    if (!shouldRedirectToLogin) {
      return;
    }

    captureAcquisitionSession({ location });
    if (SIGNUP_ENTRY_PATHS.has(location.pathname)) {
      markGrowthSignupStarted();
    }
    loginWithRedirect(buildLoginRedirectOptions(location));
  }, [
    location,
    loginWithRedirect,
    shouldRedirectToLogin,
  ]);

  if (isLocalAuthBypass) {
    return children;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-neutral-50 dark:bg-neutral-950">
        <div className="text-center">
          {/* Logo */}
          <div className="mb-8">
            <img
              src="/rely_transparente.png"
              alt="Rely"
              className="mx-auto h-12 w-auto invert dark:invert-0"
            />
          </div>

          {/* Spinner */}
          <div className="relative mb-6">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-neutral-200 border-t-secondary-500"></div>
          </div>

          {/* Text */}
          <p className="text-sm font-medium text-neutral-500">
            {t('auth.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-neutral-50 dark:bg-neutral-950">
        <div className="mx-auto max-w-md p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-error-100">
              <svg
                className="h-8 w-8 text-error-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <h2 className="mb-2 text-xl font-semibold text-neutral-900">
            {t('auth.authError')}
          </h2>
          <p className="mb-6 text-neutral-500">{error.message}</p>
          <button
            onClick={() => loginWithRedirect(buildLoginRedirectOptions(location))}
            className="inline-flex items-center justify-center rounded-lg bg-secondary-500 px-6 py-3 font-medium text-white transition-colors hover:bg-secondary-600"
          >
            {t('auth.retry')}
          </button>
        </div>
      </div>
    );
  }

  // If not authenticated and NOT on an auth route, redirect directly to Auth0
  if (shouldRedirectToLogin) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-neutral-50 dark:bg-neutral-950">
        <div className="text-center">
          <div className="mb-8">
            <img
              src="/rely_transparente.png"
              alt="Rely"
              className="mx-auto h-12 w-auto invert dark:invert-0"
            />
          </div>
          <div className="relative mb-6">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-neutral-200 border-t-secondary-500"></div>
          </div>
          <p className="text-sm font-medium text-neutral-500">
            {t('auth.redirecting')}
          </p>
        </div>
      </div>
    );
  }

  // If not authenticated but ON an auth route, show the auth pages
  // If authenticated, show the children (protected routes)
  return children;
};

export default AuthWrapper;
